<script setup lang="ts">
import type inputFullPhone from '@/components/base/form/InputFullPhone.vue';
import type { UpdatePixKey } from '@/types/UserData';

type Props = {
  showSaveButton?: boolean;
};

const { showSaveButton = true } = defineProps<Props>();

const { $containment } = useNuxtApp();
const { t } = useTexts();

const { openToaster } = useLayoutStore();
const { openDepositModal } = usePayments();
const { pixKey } = storeToRefs(useUserDataStore());
const { updatePixKeyV2, getUserPixKey } = useUserDataStore();

const { userEmail, userPhone, userPhoneDdi, userDocument, userHasDocument } =
  useUserData();

const { isMobile } = useLayout();

const isLoading = ref<boolean>(false);
const keyType = computed<string>(() => pixKey.value.type);
const keyValue = computed<string>(() => pixKey.value.value);

const formFieldType = ref<string>('');
const formFieldValue = ref<string>('');

const initialValueKeyValue = computed(() => {
  if (formFieldType.value === 'PHONE' && !userPhone.value) {
    return keyValue.value ? keyValue.value : '';
  }

  return keyType.value === 'RANDOM' ? keyValue.value : '';
});

const inputPhoneRef = ref<InstanceType<typeof inputFullPhone> | null>(null);

const selectedPhoneNumber = computed(() => {
  return inputPhoneRef.value?.selectedPhoneNumber ?? '';
});

const selectedPhoneDdi = computed(() => {
  return inputPhoneRef.value?.selectedPhoneDdi ?? '';
});

const showInputPhone = computed<boolean>(() => {
  return (
    formFieldType.value === 'PHONE' && (!userPhone.value || !userPhoneDdi.value)
  );
});

const showInputKeyValue = computed<boolean>(() => {
  return formFieldType.value === 'RANDOM';
});

const maskType = computed<string | string[]>(() => {
  if (formFieldType.value === 'PHONE') {
    return ['(##) ####-####', '(##) #####-####'];
  }

  if (formFieldType.value === 'CPF') {
    return '###.###.###-##';
  }

  return '';
});

async function onUpdatePixKeySubmit() {
  await $containment.handle('update_data', () => {
    sendForm();
  });
}

async function sendForm() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  if (pixKeyError.value) {
    openToaster({
      message: pixKeyError.value,
      type: 'error',
    });

    return;
  }

  isLoading.value = true;

  let finalKey;

  if (formFieldType.value === 'CPF') {
    finalKey = removeMask(formFieldValue.value);
  } else {
    finalKey = formFieldValue.value;
  }

  const data: UpdatePixKey = {
    pix_key_value: finalKey,
    pix_key_type: formFieldType.value,
  };

  if (
    formFieldType.value === 'PHONE' &&
    selectedPhoneNumber.value &&
    selectedPhoneDdi.value
  ) {
    data.phone_number = selectedPhoneNumber.value;
    data.phone_ddi = selectedPhoneDdi.value;
  }

  if ($kyc?.current?.id) {
    data.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    data.password_check = temporaryPassword.get();
  }

  await updatePixKeyV2(data)
    .then((res) => {
      if (!res.data.success) {
        openToaster({
          message: t('account.user_data.errors.key_error'),
          type: 'error',
        });

        return;
      }

      openToaster({
        message: t('account.user_data.update_pix_success'),
        type: 'success',
      });
    })
    .catch(() => {
      openToaster({
        message: t('account.user_data.errors.key_error'),
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function removeMask(str: string, number?: boolean) {
  if (number) {
    return str.replace(/[^\d]/gi, '');
  }

  return str.replace(/[^\w]/gi, '');
}

onMounted(() => {
  if (!pixKey.value.type) {
    getUserPixKey();
  }
});

watch(
  () => formFieldType.value,
  (typeValue) => {
    if (typeValue === 'CPF') {
      formFieldValue.value = userDocument.value;
    }
    if (typeValue === 'EMAIL') {
      formFieldValue.value = userEmail.value;
    }
    if (typeValue === 'PHONE') {
      if (userPhoneDdi.value && userPhone.value) {
        formFieldValue.value = userPhoneDdi.value + userPhone.value;
      }
    }
  },
);

watch(
  () => [userPhone.value, userPhoneDdi.value],
  ([phone, ddi]) => {
    if (formFieldType.value === 'PHONE') {
      if (!ddi || !phone) return;
      formFieldValue.value = ddi + phone;
    }
  },
);

watch(
  () => [selectedPhoneNumber.value, selectedPhoneDdi.value],
  ([phone, ddi]) => {
    if (
      formFieldType.value === 'PHONE' &&
      (!userPhone.value || !userPhoneDdi.value)
    ) {
      if (!ddi || !phone) return;
      formFieldValue.value = ddi + phone;
    }
  },
);

const pixKeyError = computed(() => {
  if (formFieldType.value === 'PHONE') {
    if (!userPhone.value || !userPhoneDdi.value) {
      if (!selectedPhoneNumber.value) {
        return t('account.user_data.errors.key_phone_error');
      }
      if (!selectedPhoneDdi.value) {
        return t('account.user_data.errors.key_ddi_error');
      }
    }
  }

  if (!formFieldValue.value || formFieldValue.value === '') {
    return t('account.user_data.errors.key_value_error');
  }

  return '';
});

const selectedKeyType = computed(() => {
  return formFieldType.value;
});

const selectedKeyValue = computed(() => {
  return formFieldValue.value;
});

function cpfMask(cpf: string) {
  if (!cpf || cpf === '') return '';

  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/gi, '$1.$2.$3-$4');
}

defineExpose({
  selectedKeyType,
  selectedKeyValue,
  selectedPhoneNumber,
  selectedPhoneDdi,
  pixKeyError,
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <template v-if="userHasDocument">
      <form
        :class="$style.componentBox"
        @submit.prevent="onUpdatePixKeySubmit"
      >
        <div
          class="max-w-full md:max-w-40"
          :class="$style.formGroup"
        >
          <label for="fieldType">
            {{ $t('account.user_data.key_type') }}
          </label>

          <BaseFormSelectField
            id="fieldType"
            v-model="formFieldType"
            :initial-value="keyType"
            name="fieldType"
            :options="[
              { value: 'CPF', label: 'CPF' },
              { value: 'PHONE', label: 'Telefone' },
              { value: 'EMAIL', label: 'E-mail' },
              { value: 'RANDOM', label: 'Chave Aleatória' },
            ]"
          />
        </div>

        <div
          v-if="showInputPhone"
          class="max-w-full md:max-w-80"
          :class="$style.formGroup"
        >
          <label for="keyValue">
            {{ $t('account.user_data.key') }}
          </label>

          <BaseFormInputFullPhone ref="inputPhoneRef" />
        </div>

        <div
          v-else-if="!showInputKeyValue"
          class="max-w-full md:max-w-80"
          :class="$style.formGroup"
        >
          <label
            v-if="!isMobile"
            for="keyValue"
          >
            {{ $t('account.user_data.key') }}
          </label>
          <!-- A classe estável (não-módulo) existe pra quem embute este
               componente conseguir vestir o campo: `inputInfo` é CSS module e
               sai hasheada, então `:deep(.inputInfo)` não casa de fora. -->
          <div
            class="pix-key-info"
            :class="$style.inputInfo"
          >
            {{ isMobile ? $t('account.user_data.key') : formFieldType }}:
            <strong class="text-primary">
              {{ formFieldValue }}
            </strong>
          </div>
        </div>

        <div
          v-else
          class="max-w-full md:max-w-80"
          :class="$style.formGroup"
        >
          <label for="keyValue">
            {{ $t('account.user_data.key') }}
          </label>

          <BaseFormInput
            id="keyValue"
            v-model="formFieldValue"
            autocomplete="off"
            :data-maska="maskType"
            :initial-value="initialValueKeyValue"
            name="keyValue"
            :placeholder="$t('account.user_data.key')"
            :readonly="!showInputKeyValue"
            type="text"
          />
        </div>

        <div
          :class="$style.thirdAlert"
          v-html="
            $t('account.user_data.pix_info', { cpf: cpfMask(userDocument) })
          "
        />

        <div
          v-if="showSaveButton"
          class="flex justify-between items-start w-full min-w-[50%]"
        >
          <button
            :class="$style.btnSubmit"
            type="submit"
          >
            <NuxtIcon name="check" />
            {{ $t('account.save') }}
          </button>
        </div>
      </form>
    </template>

    <template v-else>
      <div
        :class="$style.makeYourDeposit"
        @click="openDepositModal()"
      >
        <span class="cursor-pointer">
          {{ $t('account.user_data.make_your_deposit') }}
        </span>
        <button :class="$style.btnDeposit">
          {{ $t('header.deposit') }}
        </button>
      </div>
    </template>
  </ClientOnly>
</template>

<style module lang="scss">
.componentBox {
  @apply w-full flex items-start justify-start flex-wrap gap-4;

  .formGroup {
    @apply flex-1 w-full relative min-w-[50%] md:min-w-[20%];

    label {
      @apply text-xs font-medium;
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;
  }

  .inputInfo {
    @apply text-xs bg-primary/10 text-primary rounded px-3 py-2 h-auto md:h-[50px] flex items-center justify-start gap-2;
  }

  .thirdAlert {
    @apply text-warning text-xs mb-2 px-0.5 w-full;

    strong {
      @apply font-bold whitespace-nowrap;
    }
  }
}

.makeYourDeposit {
  @apply flex flex-col md:flex-row justify-center md:justify-between items-center gap-2 border border-dotted border-texts/40 text-texts/50 rounded-lg py-5 px-6 cursor-pointer text-center md:text-left;

  .btnDeposit {
    @apply bg-button-bg text-button-text font-semibold px-4 py-2 rounded-md text-sm cursor-pointer;
  }
}
</style>
